import { useState, useCallback, useRef, useEffect } from 'react';
import {
  MeetingSession,
  MeetingSessionConfiguration,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  AudioVideoObserver,
} from 'amazon-chime-sdk-js';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import i18n from 'i18next';
import type { MeetingStatus, VideoSessionCredentials, JoinOptions } from '../types';

interface UseMeetingProps {
  onMeetingEnded?: () => void;
  audioElementRef?: React.RefObject<HTMLAudioElement | null>;
}

interface UseMeetingReturn {
  meetingSession: MeetingSession | null;
  meetingStatus: MeetingStatus;
  join: (videoSession: VideoSessionCredentials, options?: JoinOptions) => Promise<void>;
  leave: () => Promise<void>;
}

const isDev = import.meta.env.DEV;

export function useMeeting({
  onMeetingEnded,
  audioElementRef: externalAudioRef,
}: UseMeetingProps = {}): UseMeetingReturn {
  const [meetingSession, setMeetingSession] = useState<MeetingSession | null>(
    null
  );
  const [meetingStatus, setMeetingStatus] = useState<MeetingStatus>('idle');
  // Use ref for status check inside join to avoid recreating callback on every status change
  const meetingStatusRef = useRef<MeetingStatus>(meetingStatus);
  const sessionRef = useRef<MeetingSession | null>(null);
  const observerRef = useRef<AudioVideoObserver | null>(null);
  // Use external ref if provided (DOM-mounted element), otherwise fall back to internal ref
  const internalAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioElementRef = externalAudioRef ?? internalAudioRef;

  // Keep status ref in sync for stable callback reference
  useEffect(() => {
    meetingStatusRef.current = meetingStatus;
  }, [meetingStatus]);

  const join = useCallback(
    async (videoSession: VideoSessionCredentials, options?: JoinOptions) => {
      // Guard against concurrent join calls (e.g., double-click)
      // Use ref to check status without adding it to callback deps
      const currentStatus = meetingStatusRef.current;
      if (currentStatus !== 'idle' && currentStatus !== 'failed' && currentStatus !== 'permission-denied') {
        return;
      }

      if (!videoSession.meeting || !videoSession.attendee) {
        throw new Error('Missing meeting or attendee credentials');
      }

      // Clean up any existing session from a previous failed attempt (e.g., "Try Again")
      // This prevents resource leaks from sessions that were created but never properly torn down
      if (sessionRef.current) {
        try {
          if (observerRef.current) {
            sessionRef.current.audioVideo.removeObserver(observerRef.current);
            observerRef.current = null;
          }
          // Release media devices before stopping session
          await sessionRef.current.audioVideo.stopVideoInput();
          await sessionRef.current.audioVideo.stopAudioInput();
          sessionRef.current.audioVideo.stop();
        } catch {
          // Ignore cleanup errors from previous session
        }
        sessionRef.current = null;
      }

      // Track permission denial locally to avoid stale state in outer catch
      let isPermissionDenied = false;

      try {
        setMeetingStatus('connecting');

        const logger = new ConsoleLogger(
          'ChimeMeeting',
          isDev ? LogLevel.INFO : LogLevel.ERROR
        );
        const deviceController = new DefaultDeviceController(logger);

        const configuration = new MeetingSessionConfiguration(
          videoSession.meeting,
          videoSession.attendee
        );

        const session = new DefaultMeetingSession(
          configuration,
          logger,
          deviceController
        );
        sessionRef.current = session;

        // Set up observer and store ref for cleanup
        const observer: AudioVideoObserver = {
          audioVideoDidStart: () => {
            setMeetingStatus('connected');
          },
          audioVideoDidStop: async () => {
            // Meeting ended (provider ended call, or Chime deleted meeting)
            // Clean up media devices to release camera/microphone hardware
            try {
              await session.audioVideo.stopVideoInput();
              await session.audioVideo.stopAudioInput();
            } catch {
              // Ignore errors - session may already be torn down
            }
            sessionRef.current = null;
            setMeetingSession(null);
            setMeetingStatus('disconnected');
            onMeetingEnded?.();
          },
          audioVideoDidStartConnecting: (reconnecting) => {
            setMeetingStatus(reconnecting ? 'reconnecting' : 'connecting');
          },
        };

        observerRef.current = observer;
        session.audioVideo.addObserver(observer);

        // Request media permissions first
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          });
          stream.getTracks().forEach((track) => track.stop());
        } catch (permissionError) {
          const errorName =
            permissionError instanceof Error ? permissionError.name : 'Unknown';

          if (errorName === 'NotAllowedError') {
            isPermissionDenied = true;
            toast.error(
              i18n.t(
                'videoSession.errors.permissionDenied',
                'Camera and microphone access denied'
              ),
              {
                description: i18n.t(
                  'videoSession.errors.permissionDeniedDescription',
                  'Please allow camera and microphone access in your browser settings to join the video call.'
                ),
                duration: 8000,
              }
            );
            setMeetingStatus('permission-denied');
            throw permissionError;
          } else if (errorName === 'NotFoundError') {
            toast.warning(
              i18n.t(
                'videoSession.errors.noDevices',
                'No camera or microphone found'
              ),
              {
                description: i18n.t(
                  'videoSession.errors.noDevicesDescription',
                  'You can still join the call, but others may not see or hear you.'
                ),
                duration: 6000,
              }
            );
          } else {
            // Only capture unexpected media errors to Sentry
            Sentry.captureException(permissionError, {
              tags: { component: 'useMeeting', action: 'mediaPermissions' },
            });
            toast.error(
              i18n.t(
                'videoSession.errors.mediaAccessFailed',
                'Failed to access media devices'
              ),
              {
                description: i18n.t(
                  'videoSession.errors.mediaAccessFailedDescription',
                  'Please check your browser settings and try again.'
                ),
                duration: 6000,
              }
            );
          }
        }

        // Start audio input with default device - useDevices will handle full device management
        // On iOS, skipDeviceSelection allows the caller to initialize audio input separately
        // within a click handler to preserve user gesture context for later setSinkId calls
        if (!options?.skipDeviceSelection) {
          const audioInputs = await session.audioVideo.listAudioInputDevices();
          if (audioInputs.length > 0) {
            await session.audioVideo.startAudioInput(audioInputs[0].deviceId);
          }
        }

        // Bind audio element for remote audio output
        // Use DOM-mounted element if provided (recommended for iOS Safari autoplay policies)
        // Otherwise fall back to detached Audio() element
        let audioElement = audioElementRef.current;
        if (!audioElement) {
          audioElement = new Audio();
          audioElement.autoplay = true;
          internalAudioRef.current = audioElement;
        }
        await session.audioVideo.bindAudioElement(audioElement);

        // Start the session
        session.audioVideo.start();

        setMeetingSession(session);
      } catch (error) {
        // Only capture if not already captured (permission errors are captured in inner catch)
        if (!isPermissionDenied) {
          Sentry.captureException(error, {
            tags: { component: 'useMeeting', action: 'joinMeeting' },
          });
          setMeetingStatus('failed');
        }
        throw error;
      }
    },
    [onMeetingEnded]
  );

  const leave = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) return;

    try {
      // Remove observer before stopping to prevent state updates after cleanup
      if (observerRef.current) {
        session.audioVideo.removeObserver(observerRef.current);
        observerRef.current = null;
      }

      // Clean up audio element (only if we created it internally)
      if (internalAudioRef.current) {
        internalAudioRef.current.pause();
        internalAudioRef.current.srcObject = null;
        internalAudioRef.current = null;
      } else if (externalAudioRef?.current) {
        // For externally provided audio element, just pause but don't null it
        externalAudioRef.current.pause();
        externalAudioRef.current.srcObject = null;
      }

      // Release media devices to turn off camera/microphone hardware indicators
      // This must happen before session.audioVideo.stop() which destroys the device controller
      await session.audioVideo.stopVideoInput();
      await session.audioVideo.stopAudioInput();

      session.audioVideo.stop();
      setMeetingSession(null);
      sessionRef.current = null;
      setMeetingStatus('idle');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'useMeeting', action: 'leaveMeeting' },
      });
    }
  }, []);

  // Cleanup on unmount - ensures session is properly terminated if user navigates away
  useEffect(() => {
    return () => {
      void leave();
    };
  }, [leave]);

  return {
    meetingSession,
    meetingStatus,
    join,
    leave,
  };
}
